import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel } from "../../components/panel/panel.jsx";
import Loader from "../../components/Loader.tsx";
import dayjs from "dayjs";
import {
  getAllHolidays,
  exportHolidaysExcel,
  getAllBalancesPaginated,
} from "../../services/holidays.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import FilterHolidays from "../../components/holidays/FilterHolidays.tsx";
import TableHolidays, { type Holiday } from "../../components/holidays/TableHolidays.tsx";
import PaginationHolidays from "../../components/holidays/PaginationHolidays.tsx";
import ImportHolidaysExcel from "../../components/holidays/ImportHolidaysExcel.tsx";
import HolidayBalancesFilteredTable from "../../components/holidays/HolidayBalancesFilteredTable.tsx";
import {
  setHolidays,
  setTotal,
  setPage,
  setSortBy,
  setDefaultSort,
} from "../../store/slices/holidaysSlice.ts";
import {
  setBalances,
  setTotal as setBalancesTotal,
  setPage as setBalancesPage,
  setSortBy as setBalancesSortBy,
  setDefaultSort as setBalancesDefaultSort,
} from "../../store/slices/holidayBalancesSlice.ts";

interface HolidayParams {
  limit: number;
  offset: number;
  param: string;
  sort: string;
  order: string;
  status: string;
  holidayStartDate: string;
  holidayEndDate: string;
  branchId: string;
  vacationType: string;
}

const RoleTabs = ({ 
  activeTab, 
  onTabChange, 
  showTabs 
}: { 
  activeTab: 'directBoss' | 'vacationsResponsible'; 
  onTabChange: (tab: 'directBoss' | 'vacationsResponsible') => void;
  showTabs: boolean;
}) => {
  if (!showTabs) return null;

  return (
    <div className="mb-4">
      <ul className="nav nav-pills gap-2" role="tablist">
        <li className="nav-item " role="presentation">
          <button
            className={`nav-link  ${activeTab === 'directBoss' ? 'active ' : 'bg-gray-100'}`}
            onClick={() => onTabChange('directBoss')}
            type="button"
            role="tab"
          >
            <i className="fas fa-user-tie me-2"></i>
            Jefe Directo
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'vacationsResponsible' ? 'active' : 'bg-gray-100'}`}
            onClick={() => onTabChange('vacationsResponsible')}
            type="button"
            role="tab"
          >
            <i className="fas fa-user-check me-2"></i>
            Responsable de Vacaciones
          </button>
        </li>
      </ul>
    </div>
  );
};

const AllHolidays = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [activeTab, setActiveTab] = useState<'directBoss' | 'vacationsResponsible'>('directBoss');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  useAppSettings();

  const { holidays, total, page, limit, query, sortBy, defaultSort } =
    useSelector((state: RootState) => state.holidays);
  
  const { balances, total: balancesTotal, page: balancesPage, limit: balancesLimit, sortBy: balancesSortBy, defaultSort: balancesDefaultSort } =
    useSelector((state: RootState) => state.holidayBalances);

  const { partner } = useSelector((state: RootState) => state.auth);
  const { bossId } = useParams();

  // Determinar si el usuario es responsable de vacaciones + jefe directo
  const isVacationsResponsibleAndDirectBoss = useMemo(() => {
    if (!partner) return false;
    const isDirectBoss = !!bossId;
    const isVacationsResponsible = partner.isVacationsResponsible;
    const isAdmin = partner.candidate?.vacancy?.position?.name === 'Administrador';
    const isLeader = partner.candidate?.vacancy?.position?.name === 'Lider de sucursal';
    
    return isVacationsResponsible && (isDirectBoss || isAdmin || isLeader);
  }, [partner, bossId]);

  // Determinar qué tabla mostrar basado en el filtro de tipo de vacaciones
  const vacationType = useMemo(() => {
    return query.includes("vacationType=")
      ? query.split("vacationType=")[1]?.split("&")[0] || "all"
      : "all";
  }, [query]);

  const showBalancesTable = useMemo(() => {
    return vacationType === "pendingDays" || vacationType === "usedVacations";
  }, [vacationType]);

  const holidayParams = useMemo((): HolidayParams & { bossId?: number; pendingApproval?: boolean } => {
    const offset = page * limit;
    const param = query.includes("param=")
      ? query.split("param=")[1]?.split("&")[0] || ""
      : "";
    const status = query.includes("status=")
      ? query.split("status=")[1]?.split("&")[0] || "all"
      : "all";
    const holidayStartDate = query.includes("holidayStartDate=")
      ? query.split("holidayStartDate=")[1]?.split("&")[0] || ""
      : "";
    const holidayEndDate = query.includes("holidayEndDate=")
      ? query.split("holidayEndDate=")[1]?.split("&")[0] || ""
      : "";
    const branchId = query.includes("branchId=")
      ? query.split("branchId=")[1]?.split("&")[0] || "all"
      : "all";
    const vacationType = query.includes("vacationType=")
      ? query.split("vacationType=")[1]?.split("&")[0] || "all"
      : "all";

    const params: HolidayParams & { bossId?: number; pendingApproval?: boolean } = {
      limit,
      offset,
      param,
      sort: sortBy,
      order: defaultSort.toUpperCase(),
      status,
      holidayStartDate,
      holidayEndDate,
      branchId,
      vacationType,
    };

    // Lógica para determinar qué endpoint usar
    if (isVacationsResponsibleAndDirectBoss) {
      if (activeTab === 'directBoss' && bossId) {
        // Tab "Jefe Directo": usar endpoint by-direct-boss
        params.bossId = Number(bossId);
      } else if (activeTab === 'vacationsResponsible') {
        // Tab "Responsable de Vacaciones": usar endpoint general con status=approved y pendingApproval
        params.status = 'approved';
        params.pendingApproval = true;
      }
    } else if (bossId) {
      // Usuario normal con bossId
      params.bossId = Number(bossId);
    } else if (partner?.isVacationsResponsible && !bossId) {
      // Usuario solo responsable de vacaciones (sin ser jefe directo)
      params.status = 'approved';
      params.pendingApproval = true;
    }

    return params;
  }, [page, limit, query, sortBy, defaultSort, bossId, isVacationsResponsibleAndDirectBoss, activeTab, partner]);

  const fetchHolidays = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // getAllHolidays ya maneja el endpoint by-direct-boss cuando se pasa bossId
      const response = await getAllHolidays(holidayParams);
      dispatch(setHolidays(response.data || []));
      dispatch(setTotal(response.total || 0));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Error desconocido al cargar vacaciones";
      setError(errorMessage);
      console.error("Error fetching holidays:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, holidayParams]);

  const fetchBalances = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const offset = balancesPage * balancesLimit;
      const branchId = query.includes("branchId=")
        ? query.split("branchId=")[1]?.split("&")[0] || "all"
        : "all";
      
      const params = {
        limit: balancesLimit,
        offset,
        param: query.includes("param=")
          ? query.split("param=")[1]?.split("&")[0] || ""
          : "",
        sort: balancesSortBy,
        order: balancesDefaultSort,
        branchId,
        vacationType,
        ...(bossId && { bossId: Number(bossId) }),
      };


      const response = await getAllBalancesPaginated(params);
      
      // Ya no necesitamos filtrar en el frontend, el backend lo hace
      dispatch(setBalances(response.data || []));
      dispatch(setBalancesTotal(response.total || 0));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Error desconocido al cargar balances";
      setError(errorMessage);
      console.error("Error fetching balances:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, balancesPage, balancesLimit, balancesSortBy, balancesDefaultSort, query, bossId, vacationType]);

  const handleSort = useCallback(
    (column: string): void => {
      const sortOrder =
        sortBy !== column ? "asc" : defaultSort === "asc" ? "desc" : "asc";

      dispatch(setSortBy(column));
      dispatch(setPage(0));
      dispatch(setDefaultSort(sortOrder));
    },
    [dispatch, sortBy, defaultSort]
  );

  const handleReview = useCallback(
    (holiday: Holiday): void => {
      navigate(`/colaboradores/aprobar-vacaciones/${holiday.id}`);
    },
    [navigate]
  );

  const handleTabChange = useCallback(
    (tab: 'directBoss' | 'vacationsResponsible'): void => {
      setActiveTab(tab);
      dispatch(setPage(0)); // Reset page when changing tabs
    },
    [dispatch]
  );

  useEffect(() => {
    if (showBalancesTable) {
      fetchBalances();
    } else {
      fetchHolidays();
    }
  }, [showBalancesTable, fetchHolidays, fetchBalances]);

  // Sincronizar páginas cuando cambie el tipo de vista
  useEffect(() => {
    if (showBalancesTable) {
      dispatch(setBalancesPage(page));
      dispatch(setBalancesSortBy(sortBy));
      dispatch(setBalancesDefaultSort(defaultSort));
    } else {
      dispatch(setPage(balancesPage));
      dispatch(setSortBy(balancesSortBy));
      dispatch(setDefaultSort(balancesDefaultSort));
    }
  }, [showBalancesTable, dispatch, page, balancesPage, sortBy, balancesSortBy, defaultSort, balancesDefaultSort]);

  return (
    <Layout
      children={
        <>
          <Title
            baseTitle="Vacaciones"
            basePath="/colaboradores/vacaciones"
            title={
              showBalancesTable 
                ? vacationType === "pendingDays" 
                  ? "Empleados con días pendientes"
                  : "Empleados que han usado vacaciones"
                : isVacationsResponsibleAndDirectBoss
                  ? activeTab === 'vacationsResponsible'
                    ? "Vacaciones por Aprobar (Responsable)"
                    : "Listado de vacaciones (Jefe Directo)"
                  : "Listado de vacaciones"
            }
          />

          <div className="m-4">
            <RoleTabs 
              activeTab={activeTab} 
              onTabChange={handleTabChange} 
              showTabs={isVacationsResponsibleAndDirectBoss} 
            />
          </div>

          <div className="card border-0 m-4">
            <div className="tab-pane fade show active" id="sucursal">
              <div className="card-body">
                <Panel>
                  <FilterHolidays>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <Link
                        to={bossId ? `/vacaciones-tabla/${bossId}` : "/vacaciones-tabla"}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "0.35rem",
                          padding: "0.4rem 0.85rem", borderRadius: "8px",
                          fontSize: "13px", fontWeight: 500, textDecoration: "none",
                          background: "#f1f5f9", color: "#475569",
                          border: "1px solid #e2e8f0", transition: "all 0.15s",
                        }}
                      >
                        <i className="fa fa-list" style={{ fontSize: 11 }} /> Listado
                      </Link>
                      {!bossId && (
                        <>
                          <button
                            type="button"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "0.35rem",
                              padding: "0.4rem 0.85rem", borderRadius: "8px",
                              fontSize: "13px", fontWeight: 500, cursor: "pointer",
                              background: showImport ? "#dbeafe" : "#eff6ff",
                              color: "#1d4ed8", border: "1px solid #bfdbfe",
                            }}
                            onClick={() => setShowImport(!showImport)}
                          >
                            <i className="fa fa-upload" style={{ fontSize: 11 }} /> Importar
                          </button>
                          <button
                            type="button"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "0.35rem",
                              padding: "0.4rem 0.85rem", borderRadius: "8px",
                              fontSize: "13px", fontWeight: 500, cursor: "pointer",
                              background: "#f0fdf4", color: "#15803d",
                              border: "1px solid #bbf7d0",
                            }}
                            onClick={async () => {
                              try {
                                const year = new Date().getFullYear();
                                const response = await exportHolidaysExcel(year, true);
                                const blob = new Blob([response.data], {
                                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                                });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement("a");
                                link.href = url;
                                link.download = `Vacaciones_${year}_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (e) {
                                console.error("Error descargando Excel:", e);
                              }
                            }}
                          >
                            <i className="fa fa-download" style={{ fontSize: 11 }} /> Exportar
                          </button>
                        </>
                      )}
                    </div>
                  </FilterHolidays>

                  {!bossId && showImport && <ImportHolidaysExcel />}

                  {showBalancesTable ? (
                    <HolidayBalancesFilteredTable vacationType={vacationType} />
                  ) : (
                    <>
                      <TableHolidays
                        holidays={holidays ?? []}
                        sortBy={sortBy}
                        defaultSort={defaultSort}
                        onSort={handleSort}
                        onReview={handleReview}
                        error={error}
                      />
                      <PaginationHolidays />
                    </>
                  )}
                </Panel>
              </div>
            </div>
          </div>

          <Loader isLoading={loading} />
        </>
      }
    />
  );
};

export default AllHolidays;
